import { useEffect, useCallback } from 'react';

const CHECK_INTERVAL = 60_000; // 60 seconds

/**
 * Polls /version.json every 60 s (and on tab focus) and auto-reloads when the
 * deployed server version differs from the running bundle version.
 *
 * Safe guards:
 *  - Skips when either version can't be determined, to avoid false positives.
 *  - The plugin that writes version.json is skipped in dev mode.
 *  - Running version comes from <meta name="app-version"> injected at build time.
 */
export function useNewReleaseCheck() {
  const getRunningVersion = useCallback((): string | null => {
    if (import.meta.env.VITE_APP_ENV === 'development') {
      return 'dev-version';
    }

    return document.querySelector('meta[name="app-version"]')?.getAttribute('content') ?? null;
  }, []);

  const getServerVersion = useCallback(async (): Promise<string | null> => {
    if (import.meta.env.VITE_APP_ENV === 'development') {
      return 'dev-version';
    }

    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return (data.version as string) || (data.buildTime as string) || null;
      }

      return null;
    } catch {
      return null;
    }
  }, []);

  const reloadForNewRelease = useCallback(() => {
    window.location.reload();
  }, []);

  const checkForNewRelease = useCallback(async () => {
    const runningVersion = getRunningVersion();
    const serverVersion = await getServerVersion();

    if (!runningVersion || !serverVersion) return;

    if (runningVersion === serverVersion) return;

    reloadForNewRelease();
  }, [getRunningVersion, getServerVersion, reloadForNewRelease]);

  useEffect(() => {
    checkForNewRelease();
    const interval = setInterval(checkForNewRelease, CHECK_INTERVAL);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForNewRelease();
      }
    };

    const onFocus = () => {
      checkForNewRelease();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [checkForNewRelease]);
}
